import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, Send, GitBranch, Filter, Search, ChevronDown, ChevronUp,
  MessageSquare, Hash, Clock, Globe, Users, UserPlus, UserMinus,
  Image, Video, Music, FileText, QrCode, BarChart3, Bot, Brain,
  Mail, Database, Radio, Shuffle, Activity, CreditCard, Calendar,
  Sheet, Tag, Newspaper, Target, TestTube, Calculator, Gauge, Star,
  MapPin, Phone, Share2, FileImage, Sticker, ThumbsUp, Trash2,
  Eye, DollarSign, VoteIcon, User, Smartphone, Languages, Heart,
  Navigation, Package, MessageCircle, X, Briefcase, RotateCw, Percent,
  Sparkles
} from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';

interface NodeItem {
  type: string;
  label: string;
  icon: React.ReactElement;
  description: string;
  color: string;
}

interface NodeCategory {
  label: string;
  icon: React.ReactElement;
  items: NodeItem[];
  color: string;
}

const nodeTypes: Record<string, NodeCategory> = {
  triggers: {
    label: 'Trigger',
    icon: <Zap size={18} />,
    color: 'from-yellow-400 to-yellow-600',
    items: [
      { type: 'trigger-message_received', label: 'Messaggio Ricevuto', icon: <MessageSquare />, description: 'Si attiva quando arriva un messaggio', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-keyword', label: 'Parola Chiave', icon: <Hash />, description: 'Si attiva con parole specifiche', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-schedule', label: 'Programmazione', icon: <Clock />, description: 'Si attiva ad orari prestabiliti', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-webhook', label: 'Webhook', icon: <Globe />, description: 'Si attiva via chiamata HTTP', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-group_message', label: 'Messaggio Gruppo', icon: <Users />, description: 'Si attiva nei gruppi', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-group_join', label: 'Ingresso Gruppo', icon: <UserPlus />, description: 'Quando qualcuno entra in un gruppo', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-group_leave', label: 'Uscita Gruppo', icon: <UserMinus />, description: 'Quando qualcuno esce da un gruppo', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-media_received', label: 'Media Ricevuto', icon: <Image />, description: 'Si attiva con immagini/video/audio', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-location_received', label: 'Posizione Ricevuta', icon: <MapPin />, description: 'Si attiva con posizioni condivise', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-button_click', label: 'Click Pulsante', icon: <Package />, description: 'Si attiva al click di un pulsante', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-payment_received', label: 'Pagamento Ricevuto', icon: <DollarSign />, description: 'Si attiva con un pagamento', color: 'from-yellow-400 to-yellow-600' },
      { type: 'trigger-call_received', label: 'Chiamata Ricevuta', icon: <Phone />, description: 'Si attiva con chiamate in arrivo', color: 'from-yellow-400 to-yellow-600' }
    ]
  },
  actions: {
    label: 'Azioni',
    icon: <Send size={18} />,
    color: 'from-blue-400 to-blue-600',
    items: [
      { type: 'action-send_message', label: 'Invia Messaggio', icon: <MessageSquare />, description: 'Invia un messaggio di testo', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_to_group', label: 'Invia al Gruppo', icon: <Users />, description: 'Invia un messaggio a un gruppo', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_media', label: 'Invia Media', icon: <Image />, description: 'Invia immagini, video o documenti', color: 'from-blue-400 to-blue-600' },
      { type: 'action-forward_media', label: 'Inoltra Media', icon: <Share2 />, description: 'Inoltra il media ricevuto a un contatto o gruppo', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_location', label: 'Invia Posizione', icon: <MapPin />, description: 'Condividi una posizione GPS', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_contact', label: 'Invia Contatto', icon: <User />, description: 'Condividi un contatto', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_template', label: 'Invia Template', icon: <FileText />, description: 'Usa un template predefinito', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_button', label: 'Invia Pulsanti', icon: <Package />, description: 'Invia messaggio con pulsanti', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_list', label: 'Invia Lista', icon: <FileText />, description: 'Invia lista interattiva', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_carousel', label: 'Invia Carousel', icon: <FileImage />, description: 'Invia carousel di card', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_sticker', label: 'Invia Sticker', icon: <Sticker />, description: 'Invia uno sticker', color: 'from-blue-400 to-blue-600' },
      { type: 'action-send_reaction', label: 'Invia Reazione', icon: <ThumbsUp />, description: 'Reagisci a un messaggio', color: 'from-blue-400 to-blue-600' },
      { type: 'action-create_poll', label: 'Crea Sondaggio', icon: <VoteIcon />, description: 'Crea un sondaggio', color: 'from-blue-400 to-blue-600' },
      { type: 'action-request_payment', label: 'Richiedi Pagamento', icon: <CreditCard />, description: 'Richiedi un pagamento', color: 'from-blue-400 to-blue-600' },
      { type: 'action-add_to_group', label: 'Aggiungi a Gruppo', icon: <UserPlus />, description: 'Aggiungi utente a un gruppo', color: 'from-blue-400 to-blue-600' },
      { type: 'action-remove_from_group', label: 'Rimuovi da Gruppo', icon: <UserMinus />, description: 'Rimuovi utente da un gruppo', color: 'from-blue-400 to-blue-600' },
      { type: 'action-create_group', label: 'Crea Gruppo', icon: <Users />, description: 'Crea un nuovo gruppo', color: 'from-blue-400 to-blue-600' },
      { type: 'action-forward_message', label: 'Inoltra Messaggio', icon: <Share2 />, description: 'Inoltra un messaggio', color: 'from-blue-400 to-blue-600' },
      { type: 'action-delete_message', label: 'Elimina Messaggio', icon: <Trash2 />, description: 'Elimina un messaggio', color: 'from-blue-400 to-blue-600' },
      { type: 'action-mark_as_read', label: 'Segna come Letto', icon: <Eye />, description: 'Segna messaggi come letti', color: 'from-blue-400 to-blue-600' }
    ]
  },
  conditions: {
    label: 'Condizioni',
    icon: <GitBranch size={18} />,
    color: 'from-green-400 to-green-600',
    items: [
      { type: 'condition-contains', label: 'Contiene', icon: <Search />, description: 'Verifica se contiene testo', color: 'from-green-400 to-green-600' },
      { type: 'condition-equals', label: 'Uguale a', icon: <Hash />, description: 'Verifica uguaglianza esatta', color: 'from-green-400 to-green-600' },
      { type: 'condition-time_based', label: 'Basato su Tempo', icon: <Clock />, description: 'Condizioni temporali', color: 'from-green-400 to-green-600' },
      { type: 'condition-user_attribute', label: 'Attributo Utente', icon: <User />, description: 'Verifica attributi utente', color: 'from-green-400 to-green-600' },
      { type: 'condition-is_group', label: 'È un Gruppo', icon: <Users />, description: 'Verifica se è una chat di gruppo', color: 'from-green-400 to-green-600' },
      { type: 'condition-has_media', label: 'Ha Media', icon: <Image />, description: 'Verifica presenza di media', color: 'from-green-400 to-green-600' },
      { type: 'condition-message_length', label: 'Lunghezza Messaggio', icon: <FileText />, description: 'Verifica lunghezza testo', color: 'from-green-400 to-green-600' },
      { type: 'condition-is_business', label: 'È Business', icon: <Briefcase />, description: 'Verifica account business', color: 'from-green-400 to-green-600' },
      { type: 'condition-language_detection', label: 'Rileva Lingua', icon: <Languages />, description: 'Identifica la lingua', color: 'from-green-400 to-green-600' },
      { type: 'condition-sentiment_analysis', label: 'Analisi Sentiment', icon: <Heart />, description: 'Analizza il sentiment', color: 'from-green-400 to-green-600' },
      { type: 'condition-location_based', label: 'Basato su Posizione', icon: <Navigation />, description: 'Condizioni geografiche', color: 'from-green-400 to-green-600' },
      { type: 'condition-device_type', label: 'Tipo Dispositivo', icon: <Smartphone />, description: 'Verifica tipo dispositivo', color: 'from-green-400 to-green-600' },
      { type: 'condition-message_count', label: 'Conteggio Messaggi', icon: <MessageCircle />, description: 'Numero di messaggi', color: 'from-green-400 to-green-600' }
    ]
  },
  filters: {
    label: 'Filtri',
    icon: <Filter size={18} />,
    color: 'from-red-400 to-red-600',
    items: [
      { type: 'filter-user_type', label: 'Tipo Utente', icon: <User />, description: 'Filtra per tipo utente', color: 'from-red-400 to-red-600' },
      { type: 'filter-message_type', label: 'Tipo Messaggio', icon: <MessageSquare />, description: 'Filtra per tipo messaggio', color: 'from-red-400 to-red-600' },
      { type: 'filter-language', label: 'Lingua', icon: <Globe />, description: 'Filtra per lingua', color: 'from-red-400 to-red-600' },
      { type: 'filter-location', label: 'Posizione', icon: <MapPin />, description: 'Filtra per posizione', color: 'from-red-400 to-red-600' },
      { type: 'filter-group_membership', label: 'Appartenenza Gruppo', icon: <Users />, description: 'Filtra per gruppo', color: 'from-red-400 to-red-600' }
    ]
  },
  media: {
    label: 'Media',
    icon: <Image size={18} />,
    color: 'from-purple-400 to-purple-600',
    items: [
      { type: 'media-image_editor', label: 'Editor Immagini', icon: <Image />, description: 'Modifica e ottimizza immagini', color: 'from-purple-400 to-purple-600' },
      { type: 'media-video_processor', label: 'Processore Video', icon: <Video />, description: 'Elabora e comprimi video', color: 'from-purple-400 to-purple-600' },
      { type: 'media-audio_generator', label: 'Generatore Audio', icon: <Music />, description: 'Crea audio da testo', color: 'from-purple-400 to-purple-600' },
      { type: 'media-document_creator', label: 'Creatore Documenti', icon: <FileText />, description: 'Genera PDF e documenti', color: 'from-purple-400 to-purple-600' },
      { type: 'media-qr_generator', label: 'Generatore QR', icon: <QrCode />, description: 'Crea codici QR', color: 'from-purple-400 to-purple-600' },
      { type: 'media-chart_builder', label: 'Costruttore Grafici', icon: <BarChart3 />, description: 'Crea grafici e visualizzazioni', color: 'from-purple-400 to-purple-600' }
    ]
  },
  templates: {
    label: 'Template',
    icon: <FileText size={18} />,
    color: 'from-indigo-400 to-indigo-600',
    items: [
      { type: 'template-welcome', label: 'Benvenuto', icon: <Mail />, description: 'Template di benvenuto', color: 'from-indigo-400 to-indigo-600' },
      { type: 'template-order_confirmation', label: 'Conferma Ordine', icon: <FileText />, description: 'Conferma acquisti', color: 'from-indigo-400 to-indigo-600' },
      { type: 'template-appointment', label: 'Appuntamento', icon: <Calendar />, description: 'Gestione appuntamenti', color: 'from-indigo-400 to-indigo-600' },
      { type: 'template-feedback', label: 'Feedback', icon: <MessageSquare />, description: 'Richiesta feedback', color: 'from-indigo-400 to-indigo-600' },
      { type: 'template-newsletter', label: 'Newsletter', icon: <Newspaper />, description: 'Invio newsletter', color: 'from-indigo-400 to-indigo-600' },
      { type: 'template-promotion', label: 'Promozione', icon: <Tag />, description: 'Offerte e promozioni', color: 'from-indigo-400 to-indigo-600' }
    ]
  },
  chatbot: {
    label: 'Chatbot',
    icon: <Bot size={18} />,
    color: 'from-cyan-400 to-cyan-600',
    items: [
      { type: 'chatbot-ai_response', label: 'Risposta AI', icon: <Bot />, description: 'Genera risposte con AI', color: 'from-cyan-400 to-cyan-600' },
      { type: 'chatbot-intent_detection', label: 'Rileva Intent', icon: <Brain />, description: 'Identifica intenzioni', color: 'from-cyan-400 to-cyan-600' },
      { type: 'chatbot-entity_extraction', label: 'Estrai Entità', icon: <Search />, description: 'Estrai dati dal testo', color: 'from-cyan-400 to-cyan-600' },
      { type: 'chatbot-context_manager', label: 'Gestione Contesto', icon: <Database />, description: 'Mantieni contesto conversazione', color: 'from-cyan-400 to-cyan-600' },
      { type: 'chatbot-conversation_flow', label: 'Flusso Conversazione', icon: <GitBranch />, description: 'Gestisci dialoghi complessi', color: 'from-cyan-400 to-cyan-600' }
    ]
  },
  integrations: {
    label: 'Integrazioni',
    icon: <Globe size={18} />,
    color: 'from-orange-400 to-orange-600',
    items: [
      { type: 'integration-crm', label: 'CRM', icon: <Users />, description: 'Connetti al tuo CRM', color: 'from-orange-400 to-orange-600' },
      { type: 'integration-email', label: 'Email', icon: <Mail />, description: 'Invia email', color: 'from-orange-400 to-orange-600' },
      { type: 'integration-database', label: 'Database', icon: <Database />, description: 'Leggi/scrivi database', color: 'from-orange-400 to-orange-600' },
      { type: 'integration-api_call', label: 'Chiamata API', icon: <Globe />, description: 'Chiama API esterne', color: 'from-orange-400 to-orange-600' },
      { type: 'integration-google_sheets', label: 'Google Sheets', icon: <Sheet />, description: 'Integra con fogli Google', color: 'from-orange-400 to-orange-600' },
      { type: 'integration-calendar', label: 'Calendario', icon: <Calendar />, description: 'Gestisci eventi calendario', color: 'from-orange-400 to-orange-600' },
      { type: 'integration-payment_gateway', label: 'Gateway Pagamento', icon: <CreditCard />, description: 'Processa pagamenti', color: 'from-orange-400 to-orange-600' },
      { type: 'integration-social_media', label: 'Social Media', icon: <Share2 />, description: 'Pubblica sui social', color: 'from-orange-400 to-orange-600' }
    ]
  },
  analytics: {
    label: 'Analytics',
    icon: <Activity size={18} />,
    color: 'from-pink-400 to-pink-600',
    items: [
      { type: 'analytics-track_event', label: 'Traccia Evento', icon: <Activity />, description: 'Registra eventi custom', color: 'from-pink-400 to-pink-600' },
      { type: 'analytics-user_profile', label: 'Profilo Utente', icon: <User />, description: 'Aggiorna profili utente', color: 'from-pink-400 to-pink-600' },
      { type: 'analytics-conversion', label: 'Conversione', icon: <Target />, description: 'Traccia conversioni', color: 'from-pink-400 to-pink-600' },
      { type: 'analytics-report_generator', label: 'Genera Report', icon: <FileText />, description: 'Crea report dettagliati', color: 'from-pink-400 to-pink-600' },
      { type: 'analytics-ab_test', label: 'Test A/B', icon: <TestTube />, description: 'Esegui test A/B', color: 'from-pink-400 to-pink-600' },
      { type: 'analytics-metrics_calculator', label: 'Calcola Metriche', icon: <Calculator />, description: 'Calcola KPI custom', color: 'from-pink-400 to-pink-600' }
    ]
  },
  broadcast: {
    label: 'Broadcast',
    icon: <Radio size={18} />,
    color: 'from-emerald-400 to-emerald-600',
    items: [
      { type: 'broadcast-list', label: 'Broadcast Lista', icon: <Users />, description: 'Invia a lista contatti', color: 'from-emerald-400 to-emerald-600' },
      { type: 'broadcast-segment', label: 'Broadcast Segmento', icon: <Radio />, description: 'Invia a segmento specifico', color: 'from-emerald-400 to-emerald-600' },
      { type: 'broadcast-scheduled', label: 'Broadcast Programmato', icon: <Clock />, description: 'Programma invii futuri', color: 'from-emerald-400 to-emerald-600' },
      { type: 'broadcast-dynamic', label: 'Broadcast Dinamico', icon: <Sparkles />, description: 'Contenuto personalizzato', color: 'from-emerald-400 to-emerald-600' }
    ]
  },
  flow: {
    label: 'Controllo Flusso',
    icon: <GitBranch size={18} />,
    color: 'from-violet-400 to-violet-600',
    items: [
      { type: 'delay-fixed', label: 'Ritardo Fisso', icon: <Clock />, description: 'Attendi tempo specifico', color: 'from-amber-400 to-amber-600' },
      { type: 'delay-random', label: 'Ritardo Casuale', icon: <Shuffle />, description: 'Attendi tempo casuale', color: 'from-amber-400 to-amber-600' },
      { type: 'delay-business_hours', label: 'Orari Lavorativi', icon: <Briefcase />, description: 'Attendi orari ufficio', color: 'from-amber-400 to-amber-600' },
      { type: 'router-random', label: 'Router Casuale', icon: <Shuffle />, description: 'Instrada casualmente', color: 'from-violet-400 to-violet-600' },
      { type: 'router-round_robin', label: 'Round Robin', icon: <RotateCw />, description: 'Distribuisci equamente', color: 'from-violet-400 to-violet-600' },
      { type: 'router-percentage', label: 'Percentuale', icon: <Percent />, description: 'Dividi per percentuale', color: 'from-violet-400 to-violet-600' },
      { type: 'router-priority', label: 'Priorità', icon: <Star />, description: 'Instrada per priorità', color: 'from-violet-400 to-violet-600' }
    ]
  }
};

const ModernSidebar: React.FC = () => {
  const [search, setSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['triggers', 'actions']);

  const onDragStart = (event: React.DragEvent, nodeType: string, item: NodeItem) => {
    const [mainType, subType] = nodeType.split('-');
    const nodeData = {
      nodeType: mainType,
      nodeSubType: subType,
      label: item.label
    };
    event.dataTransfer.setData('application/reactflow', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const filteredCategories = useMemo(() => {
    if (!search) return nodeTypes;

    const filtered: Record<string, NodeCategory> = {};
    Object.entries(nodeTypes).forEach(([key, category]) => {
      const filteredItems = category.items.filter(item =>
        item.label.toLowerCase().includes(search.toLowerCase()) ||
        item.description.toLowerCase().includes(search.toLowerCase())
      );
      if (filteredItems.length > 0) {
        filtered[key] = { ...category, items: filteredItems };
      }
    });
    return filtered;
  }, [search]);

  return (
    <Tooltip.Provider delayDuration={200}>
      <motion.div
        initial={{ x: -300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className="h-full w-80 bg-gray-900 border-r border-gray-800 overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-3">
            Componenti Automazione
          </h2>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca componenti..."
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-whatsapp-green transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Categories */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {Object.entries(filteredCategories).map(([key, category]) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-800 rounded-lg overflow-hidden"
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(key)}
                className="w-full p-3 flex items-center justify-between hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className={`p-1 rounded bg-gradient-to-r ${category.color}`}>
                    {category.icon}
                  </div>
                  <span className="text-white font-medium">{category.label}</span>
                  <span className="text-xs text-gray-400">({category.items.length})</span>
                </div>
                {expandedCategories.includes(key) ? (
                  <ChevronUp size={18} className="text-gray-400" />
                ) : (
                  <ChevronDown size={18} className="text-gray-400" />
                )}
              </button>

              {/* Category Items */}
              <AnimatePresence>
                {expandedCategories.includes(key) && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="p-2 space-y-1">
                      {category.items.map((item) => (
                        <Tooltip.Root key={item.type}>
                          <Tooltip.Trigger asChild>
                            <motion.div
                              draggable
                              onDragStart={(e: React.DragEvent) => onDragStart(e, item.type, item)}
                              whileHover={{ scale: 1.02 }}
                              whileDrag={{ scale: 1.1, rotate: 2 }}
                              className="p-3 bg-gray-700 rounded-lg cursor-move hover:bg-gray-600 transition-all group"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded bg-gradient-to-r ${item.color} opacity-80 group-hover:opacity-100 transition-opacity`}>
                                  {React.cloneElement(item.icon, { size: 16 })}
                                </div>
                                <div className="flex-1">
                                  <h4 className="text-sm font-medium text-white">
                                    {item.label}
                                  </h4>
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {item.description}
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content
                              className="z-50 bg-gray-800 text-white px-3 py-2 rounded-lg shadow-xl border border-gray-700"
                              sideOffset={5}
                            >
                              <p className="text-sm font-medium">{item.label}</p>
                              <p className="text-xs text-gray-400 mt-1">{item.description}</p>
                              <p className="text-xs text-whatsapp-green mt-2">
                                Trascina nell'editor per aggiungere
                              </p>
                              <Tooltip.Arrow className="fill-gray-800" />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Footer Help */}
        <div className="p-4 border-t border-gray-800">
          <p className="text-xs text-gray-400 text-center">
            💡 Trascina i componenti nell'editor per costruire la tua automazione
          </p>
        </div>
      </motion.div>
    </Tooltip.Provider>
  );
};

export default ModernSidebar;